import { useToast } from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { doRequest } from './AccountManagement.utils';

export interface AccountManagementContextProps {
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  confirmAccount: (token: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const AccountManagementContext = createContext<
  AccountManagementContextProps | undefined
>(undefined);

export type AccountManagementProviderProps = {
  apiBaseUrl: string;
  doFetch?: typeof doRequest;
};

export const AccountManagementProvider: React.FC<
  PropsWithChildren<AccountManagementProviderProps>
> = ({ children, apiBaseUrl, doFetch = doRequest }) => {
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequest = useCallback(
    async <T,>(url: string, options: RequestInit): Promise<T | void> => {
      setIsLoading(true);
      setError(null);
      try {
        return await doFetch<T>(url, options);
      } catch (error) {
        const errorMessage = error?.toString() || 'Something went wrong';
        setError(errorMessage);
        showToast(errorMessage, 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [doFetch, showToast]
  );

  const forgotPassword = useCallback(
    async (email: string) =>
      handleRequest<void>(`${apiBaseUrl}/auth/forgot-password`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    [apiBaseUrl, handleRequest]
  );

  const resetPassword = useCallback(
    async (token: string, password: string) =>
      handleRequest<void>(`${apiBaseUrl}/auth/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ token, password }),
      }),
    [apiBaseUrl, handleRequest]
  );

  const confirmAccount = useCallback(
    async (token: string) =>
      handleRequest<void>(`${apiBaseUrl}/auth/verify-email-address`, {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    [apiBaseUrl, handleRequest]
  );

  return (
    <AccountManagementContext.Provider
      value={{
        forgotPassword,
        resetPassword,
        confirmAccount,
        isLoading,
        error,
      }}
    >
      {children}
    </AccountManagementContext.Provider>
  );
};

export const useAccountManagement = () => {
  const context = useContext(AccountManagementContext);
  if (!context) {
    throw new Error(
      'useAccountManagement must be used within an AccountManagementProvider'
    );
  }
  return context;
};
